import React, { useState, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { useMemberGroupList } from '../hooks/useMemberGroupList';
import { SearchSelect, SearchSelectProps, SearchSelectItem } from '@td-client-frontend/common';
import { Group, GroupRegistrationCode } from '../@types/Members';

interface PropsOverrideInterface {
  onSelect: OnGroupSelect;
  value: Group | SearchSelectItem | undefined;
}

type ExcludeFromPropsInterface = 'label' | 'onSearchChange' | 'buildTemplate' | 'onSelect' | 'value';
type OnGroupSelect = (selectedGroup: Group | SearchSelectItem) => void;
type SearchSelectPropsWithGroupOnSelect = Omit<Partial<SearchSelectProps>, ExcludeFromPropsInterface> &
  PropsOverrideInterface;
export interface GroupSearchSelectProperties extends SearchSelectPropsWithGroupOnSelect {
  onSelect: OnGroupSelect;
  includeAll?: boolean;
  label?: string;
  requiredPerm: string;
  groupRegistrationCodes?: GroupRegistrationCode;
  notTermed?: boolean;
  onSearchChange?: (search: string) => void;
  activeOnly?: boolean;
  initialSearchTerm?: string;
}

export const ALL_OPTION: SearchSelectItem = { value: null, label: 'All' };

export function GroupSearchSelect({
  onSelect,
  options,
  label = 'Groups',
  includeAll = true,
  value,
  requiredPerm,
  groupRegistrationCodes,
  onSearchChange,
  activeOnly,
  initialSearchTerm = '',
  notTermed,
  ...props
}: GroupSearchSelectProperties) {
  // Search term, debounces and uses debounced value to trigger search
  const [groupSearchTerm, setGroupSearchTerm] = useState<string>('');
  const [debouncedGroupSearch] = useDebounce<string>(groupSearchTerm, 750);
  const { values: groups, isFetching: groupsIsFetching } = useMemberGroupList({
    search: initialSearchTerm && !groupSearchTerm ? initialSearchTerm : debouncedGroupSearch,
    requiredPerm,
    groupRegistrationCodes,
    notTermed
  });

  // Whenever groups updates, reformat it to work with the SearchSelect, add all option if needed
  const displayGroups = useMemo(() => {
    if (!groups || !groups.length) {
      return [];
    }
    const result = groups
      .map(({ id, name }: Group) => ({ value: parseInt(id.toString()), label: name }))
      .concat(options || []);
    if (includeAll) {
      const all = { ...ALL_OPTION, value: groups.map((group: Group) => group.id) };
      return [all, ...result];
    }
    return result;
  }, [groups, includeAll, options]);

  const onSearchChangeHandler = (term: string): void => {
    setGroupSearchTerm(term.length >= 3 ? term : '');

    onSearchChange && onSearchChange(term);
  };

  const onGroupSelect = ({ value }: SearchSelectItem) => {
    // eslint-disable-next-line eqeqeq
    onSelect(groups?.find((group: Group) => group.id == value) || ALL_OPTION);
  };

  return (
    <SearchSelect
      {...props}
      value={value && ('id' in value ? value.id : value.value)}
      name="groupSearchSelect"
      label={label}
      options={displayGroups}
      onSearchChange={onSearchChangeHandler}
      efficientValueLength={0}
      onSelect={onGroupSelect}
      buildTemplate={(item) => <div>{item.label}</div>}
      dataIsLoading={groupsIsFetching}
      fieldClasses="truncate"
      isFiltered={false}
    />
  );
}
